import React, { useEffect, useMemo, useRef } from "react";
import { FileUpload } from "@components/FileUpload/FileUpload";
import SingleFile from "@components/FileUpload/SingleFile";
import FormItem, { getErrorMessage } from "@components/FormItem/FormItem";
import { useNotification } from "@context/NotificationContext";
import { useUploadDocument } from "@features/vessel-nomination/add/hooks/useUploadDocument";
import { getCurrentUaeDate } from "@utils/date-time";
import { toISODateStrict } from "@utils/formatters/form";
import { FormProvider, useForm, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  ATTACHMENT_EXTENSIONS,
  ATTACHMENT_FORMATS,
  ATTACHMENT_MAX_BYTES,
  ATTACHMENT_MAX_FILES,
} from "../constants";
import {
  DOCUMENT_TYPES,
  LIMITS,
  type DryBulkNominationFormData,
  type StoredAttachment,
} from "../schema/dryBulkNominationSchema";
import { fromUaeDay } from "../utils/uaeDate";
import DryBulkSection from "./ui/DryBulkSection";

const LANG_BASE = "vessel-nomination.dry-bulk.add.attachments";

type DateField = "issueDate" | "expiryDate";

interface DateFieldProps {
  testId: string;
  index: number;
  field: DateField;
  label: string;
  notPast?: boolean;
}

/**
 * The form keeps attachment dates as UAE calendar days (`YYYY-MM-DD`, persisted with the draft and sent as is) while
 * the date picker works on a Date: a one-field form converts between them on the local fields, never through UTC.
 */
const AttachmentDateField: React.FC<DateFieldProps> = ({
  testId,
  index,
  field,
  label,
  notPast,
}) => {
  const parent = useFormContext<DryBulkNominationFormData>();
  const path = `attachments.${index}.${field}` as const;
  const stored = useWatch({ control: parent.control, name: path }) as string | null | undefined;
  const error = getErrorMessage(parent.formState.errors as Record<string, unknown>, path);
  const empty = field === "expiryDate" ? "" : null;
  const minDate = notPast ? getCurrentUaeDate() : undefined;
  minDate?.setHours(0, 0, 0, 0);
  const local = useForm<{ date: Date | null }>({
    defaultValues: { date: stored ? fromUaeDay(stored) : null },
  });

  useEffect(() => {
    const subscription = local.watch(({ date }) => {
      const next = date ? toISODateStrict(date) : empty;
      if (next !== (stored ?? empty)) {
        parent.setValue(path, next as never, { shouldDirty: true, shouldValidate: true });
      }
    });
    return () => subscription.unsubscribe();
  }, [local, parent, path, empty, stored]);

  useEffect(() => {
    if (error) local.setError("date", { type: "manual", message: error });
    else local.clearErrors("date");
  }, [error, local]);

  return (
    <FormProvider {...local}>
      <FormItem<{ date: Date | null }, "date">
        required={field === "expiryDate"}
        testId={testId}
        name="date"
        type="date-picker"
        timePicker={false}
        minDate={minDate}
        label={label}
      />
    </FormProvider>
  );
};

interface RowProps {
  testId: string;
  index: number;
  attachment: StoredAttachment;
  onRemove: () => void;
}

const AttachmentRow: React.FC<RowProps> = ({ testId, index, attachment, onRemove }) => {
  const { t } = useTranslation();
  const documentTypeOptions = DOCUMENT_TYPES.map((value) => ({
    value,
    label: t(`${LANG_BASE}.types.${value}`),
  }));

  return (
    <div className="flex flex-col gap-3" data-testid={`${testId}-${index}`}>
      <SingleFile
        testId={`${testId}-${index}-file`}
        file={{ name: attachment.documentName ?? "", url: attachment.fileUrl }}
        fieldName={testId}
        currentFileIndex={index}
        setFiles={onRemove}
      />
      <div className="grid grid-cols-1 gap-x-6 md:grid-cols-2">
        <FormItem<DryBulkNominationFormData, `attachments.${number}.documentType`>
          required
          testId={`${testId}-${index}-type`}
          name={`attachments.${index}.documentType`}
          type="select"
          label={t(`${LANG_BASE}.type`)}
          options={documentTypeOptions}
        />
        {attachment.documentType === "OTHERS" ? (
          <FormItem<DryBulkNominationFormData, `attachments.${number}.otherSpecify`>
            required
            testId={`${testId}-${index}-other-specify`}
            name={`attachments.${index}.otherSpecify`}
            label={t(`${LANG_BASE}.other-specify`)}
            maxChars={LIMITS.OTHER_SPECIFY}
            showCharCounter
          />
        ) : (
          <div className="hidden md:block" />
        )}
        <AttachmentDateField
          testId={`${testId}-${index}-issue-date`}
          index={index}
          field="issueDate"
          label={t(`${LANG_BASE}.issue-date`)}
        />
        <AttachmentDateField
          testId={`${testId}-${index}-expiry-date`}
          index={index}
          field="expiryDate"
          label={t(`${LANG_BASE}.expiry-date`)}
          notPast
        />
      </div>
    </div>
  );
};

interface Props {
  testId: string;
}

/** VN-09 / VD-44: drop zone for jpg / png / pdf up to 10 MB, then one row per uploaded file (D4, wireframe 07.1 SA). */
const DryBulkAttachmentsSection: React.FC<Props> = ({ testId }) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const { control, setValue, clearErrors } = useFormContext<DryBulkNominationFormData>();
  const attachments = (useWatch({ control, name: "attachments" }) ?? []) as StoredAttachment[];
  const attachmentsRef = useRef(attachments);
  attachmentsRef.current = attachments;

  const replace = (next: StoredAttachment[]) => {
    clearErrors("attachments");
    setValue("attachments", next as never, { shouldDirty: true });
  };

  const upload = useUploadDocument({
    multiple: true,
    transaction: {
      module: "dry_bulk_vessel_nomination",
      screen: "DryBulkNominationForm",
      action: "upload_dry_bulk_nomination_document",
      entityType: "dry_bulk_nomination_document",
    },
    handleUploadSuccess: ({ documents }) =>
      replace([
        ...attachmentsRef.current,
        ...documents.map(({ documentUrl, documentName }) => ({
          documentType: "" as StoredAttachment["documentType"],
          otherSpecify: "",
          documentName,
          fileUrl: documentUrl,
          issueDate: null,
          expiryDate: "",
        })),
      ]),
    handleUploadError: () =>
      notify({
        type: "error",
        title: t(`${LANG_BASE}.upload-error-title`),
        description: t(`${LANG_BASE}.upload-error-description`),
      }),
  });

  const conditions = useMemo(
    () => ({
      format: ATTACHMENT_FORMATS,
      maxSizeLabel: t("upload.max-size", { size: "10MB" }),
      maxSizeBytes: ATTACHMENT_MAX_BYTES,
      accept: {},
    }),
    [t],
  );

  return (
    <DryBulkSection
      testId={`${testId}-attachments`}
      title={t("vessel-nomination.dry-bulk.add.sections.attachments")}
    >
      <div className="flex flex-col gap-4 rounded border border-[#F1F2F4] p-4">
        <FileUpload
          testId={`${testId}-attachments-upload`}
          name={`${testId}-attachments`}
          conditions={conditions}
          files={attachments.map(({ documentName, fileUrl }) => ({
            name: documentName ?? "",
            url: fileUrl,
          }))}
          setFiles={() => undefined}
          maxFiles={ATTACHMENT_MAX_FILES}
          allowedExtensions={ATTACHMENT_EXTENSIONS}
          disabled={upload.isPending}
          uploadDescription={t("upload.additional-documents-upload", {
            extensions: ATTACHMENT_FORMATS.join(", "),
            size: "10MB",
          })}
          onUpload={(files) =>
            upload.mutate({ files, documentType: "OTHERS", startIdx: attachments.length })
          }
        />
        {attachments.map((attachment, index) => (
          <AttachmentRow
            key={`${attachment.fileUrl}-${index}`}
            testId={`${testId}-attachment`}
            index={index}
            attachment={attachment}
            onRemove={() => replace(attachments.filter((_, i) => i !== index))}
          />
        ))}
      </div>
    </DryBulkSection>
  );
};

export default DryBulkAttachmentsSection;
